import React, { FC } from 'react'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import {
  BackButton,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  Tabs,
  Tooltip
} from 'front-end/src/components'
import IconCalendar from 'front-end/src/components/common/icon/IconCalendar'
import IconDotsVertical from 'front-end/src/components/common/icon/IconDotsVertical'
import IconList from 'front-end/src/components/common/icon/IconList'
import IconPeople from 'front-end/src/components/common/icon/IconPeople'
import {
  Topbar,
  TopbarCenter,
  TopbarLeft,
  TopbarRight,
  TopbarTitle
} from 'front-end/src/components/common/topbar/Topbar'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'

import { plural, t, Trans } from '@lingui/macro'
import { EventDetailActions } from './tabs/event-detail/EventDetailActions'
import dayjs from 'shared/dayjs'
import { useLocation } from 'react-router-dom'
import { useFormikContext } from 'formik'
import { EventDeleteModal } from '../EventDeleteModal'
import { Spinner } from '@chakra-ui/react'
import {
  EventDetailEditFragment,
  useCancelEventMutation
} from './EventEdit.codegen'
import { useManageEventContext } from '../ManageEventContext'
import IconTrash from 'front-end/src/components/common/icon/IconTrash'
import { brandColors } from 'shared/basicPalette'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { DiscardChangesModal } from './tabs/event-detail/DiscardChangesModal'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { useCancelRecurringEventMutation } from 'front-end/src/pages/manage-events/event-edit/RecurringEventEdit.codegen'
import { EventQueryAccessor } from 'front-end/src/pages/manage-events/event-edit/helpers'

export const ManageEventTopbar: FC<{ event: EventDetailEditFragment }> = ({
  event
}) => {
  const { isRecurringSeries, isRecurringInstance } = useManageEventContext()
  const { isSubmitting, values, dirty } =
    useFormikContext<EventDetailEditFragment>()
  const { pathname } = useLocation()
  const { open: openDiscard, currentModal: discardModal } = useModal()
  const { open: openDelete, currentModal: deleteModal } = useModal()

  const isOccurrencesTab = pathname.endsWith('/occurrences')
  const isAttendanceTab = pathname.endsWith('/attendance')

  const [cancelEventMutation, { loading: isCancelLoading }] =
    useCancelEventMutation()
  const [cancelRecurringEventMutation, { loading: isCancelRecurringLoading }] =
    useCancelRecurringEventMutation()

  const key = isRecurringSeries
    ? EventQueryAccessor.recurringEvent
    : EventQueryAccessor.event

  const cancelMutation = isRecurringSeries
    ? cancelRecurringEventMutation
    : cancelEventMutation

  const isDateRangeValid = () => {
    if (!values || !values.start_time) {
      return false
    }

    const startTime = dayjs(values.start_time)

    if (values.end_time) {
      const endTime = dayjs(values.end_time)
      return endTime.isAfter(startTime)
    }

    return startTime.isAfter(dayjs())
  }

  const getCurrentTab = () => {
    if (isOccurrencesTab) {
      return 1
    }
    if (isAttendanceTab) {
      return isRecurringSeries ? 2 : 1
    }
    return 0
  }

  const getArchiveButtonText = () => {
    if (isRecurringSeries) {
      return <Trans>Archive this event series</Trans>
    }
    if (isRecurringInstance) {
      return <Trans>Archive this event occurrence</Trans>
    }
    return <Trans>Archive this event</Trans>
  }

  return (
    <>
      <Topbar>
        <TopbarLeft>
          <Tooltip tabIndex={-1} text={<Trans>Back</Trans>}>
            <BackButton
              ml="4px"
              mr="8px"
              defaultPath={staticLinks.manageEvents}
              beforeHandleNavigation={async () => {
                if (dirty) {
                  await openDiscard(DiscardChangesModal)
                }
              }}
            />
          </Tooltip>

          <TopbarTitle>{event?.title}</TopbarTitle>
        </TopbarLeft>

        <TopbarCenter>
          <Tabs size="lg" index={getCurrentTab()}>
            <TabList borderBottom={0}>
              <Tab
                borderRadius={0}
                onClick={() =>
                  navigateTo(
                    linkBuilder.manageEvent(event.id, isRecurringSeries)
                  )
                }
              >
                <IconCalendar mr="8px" /> <Trans>Details</Trans>
              </Tab>
              {isRecurringSeries && (
                <Tab
                  borderRadius={0}
                  onClick={() =>
                    navigateTo(
                      linkBuilder.manageEvent(
                        event.id,
                        isRecurringSeries,
                        'occurrences'
                      )
                    )
                  }
                >
                  <IconList mr="8px" /> <Trans>Occurrences</Trans>
                </Tab>
              )}
              <Tab
                borderRadius={0}
                onClick={() =>
                  navigateTo(
                    linkBuilder.manageEvent(
                      event.id,
                      isRecurringSeries,
                      'attendance'
                    )
                  )
                }
              >
                <IconPeople mr="8px" /> <Trans>Attendance</Trans>
              </Tab>
            </TabList>
          </Tabs>
        </TopbarCenter>

        <TopbarRight position="relative">
          {isSubmitting && <Spinner size="md" mr="16px" />}
          <ButtonGroup>
            <EventDetailActions isDateRangeValid={isDateRangeValid()} />

            <Menu>
              <MenuButton
                variant="outline"
                boxSize="40px"
                border="1px"
                onClick={(e) => {
                  e.stopPropagation() // Stop propagating click on row
                }}
                dataTest="KebabMenu"
              >
                <IconDotsVertical boxSize="18px" />
              </MenuButton>
              <MenuList data-test="MenuList">
                <MenuItem
                  color={brandColors.red['500']}
                  icon={<IconTrash fontSize="17px" />}
                  onClick={() => {
                    openDelete(EventDeleteModal, {
                      disableActions:
                        isCancelLoading || isCancelRecurringLoading
                    }).then(async ({ notifyInvitees, emailIdentityId }) => {
                      const { data, errors } = await cancelMutation({
                        variables: {
                          eventId: event.id,
                          sendCancellationEmail: notifyInvitees,
                          emailIdentityId
                        }
                      })

                      // Prevent redirect if has errors
                      if (errors) return

                      let notifyMsg = t`Event cancelled`
                      if (
                        notifyInvitees &&
                        data?.admin?.[key]?.invitees?.length
                      ) {
                        notifyMsg = plural(
                          data?.admin?.[key]?.invitees?.length,
                          {
                            one: 'Event cancelled, sent cancellation notice to # attendee',
                            other:
                              'Event cancelled, sent cancellation notice to # attendees'
                          }
                        )
                      }
                      notification.info(notifyMsg)

                      navigateTo(staticLinks.manageEvents)
                    })
                  }}
                  data-test="ArchiveButton"
                >
                  {getArchiveButtonText()}
                </MenuItem>
              </MenuList>
            </Menu>
          </ButtonGroup>
        </TopbarRight>
      </Topbar>
      {deleteModal}
      {discardModal}
    </>
  )
}

export default ManageEventTopbar
